/**
 * RichContentInputWithMath - Versión de RichContentInput con NimbusMath integrado
 * Ejemplo de cómo integrar NimbusMath con CKEditor existente
 */

import React, { useRef, useCallback, useEffect, useState } from 'react';
import { CKEditor } from "@ckeditor/ckeditor5-react";
import { Editor as ClassicEditor } from "ckeditor5-resize-nimbus-build/build/ckeditor";
import 'mathlive';
import { MyUploadAdapter } from "../../constructor/umas/MyUploadAdapter";
import { customEditorToolbar, editorImageConfig, editorLinkConfig, listProperties, htmlSupportConfig } from "../ckEditorUtils";
import { useNimbusMath } from './useNimbusMath';
import { insertMathMLToCKEditor, preprocessMathMLForCKEditor, convertFormulaImagesToMathML, DataLatexPlugin } from './ckeditorUtils';
import NimbusPasteStyleCleanupPlugin from '../../constructor/umas/ActividadesUtils/NimbusPasteStyleCleanupPlugin';

function MyCustomUploadAdapterPlugin(editor) {
    editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
        return new MyUploadAdapter(loader);
    };
}

/**
 * RichContentInput con botón de NimbusMath integrado
 * Reemplaza MathType de WIRIS
 */
const RichContentInputWithMath = ({ 
    value = "", 
    onChange = () => {}, 
    label, 
    error = "", 
    name, 
    disabled = false, 
    placeholder = "", 
    styles = "",
    showMathButton = true
}) => {
    const editorRef = useRef(null);
    const onInsertRef = useRef(null);
    const [processedValue, setProcessedValue] = useState(value);

    // Preprocesar MathML inline a imágenes y normalizar dimensiones
    useEffect(() => {
        const processContent = async () => {
            if (value) {
                const hasMathML = /<math[^>]*>/i.test(value);
                const hasExDimensions = /(?:width|height)=["'][\d.]+ex["']/i.test(value);
                
                if (hasMathML || hasExDimensions) {
                    try {
                        const processed = await preprocessMathMLForCKEditor(value);
                        setProcessedValue(processed);
                    } catch (error) {
                        console.error('Error preprocesando MathML:', error);
                        setProcessedValue(value);
                    }
                } else {
                    setProcessedValue(value);
                }
            } else {
                setProcessedValue(value);
            }
        };
        processContent();
    }, [value]);

    const handleMathInsert = useCallback((mathml, latex) => {
        if (editorRef.current) {
            insertMathMLToCKEditor(editorRef.current, mathml, latex);
        }
    }, []);

    useEffect(() => {
        onInsertRef.current = handleMathInsert;
    }, [handleMathInsert]);

    const { open: openMathPanel } = useNimbusMath({
        position: 'bottom-right',
        mathsize: '14pt',
        mathcolor: '#0A0A0A',
        mathfont: 'monospace',
        onInsert: (mathml, latex) => {
            onInsertRef.current?.(mathml, latex);
        }
    });

    // Filtrar MathType y ChemType del toolbar si usamos NimbusMath
    const toolbarWithoutMathType = customEditorToolbar.filter(
        item => item !== 'MathType' && item !== 'ChemType'
    );

    return (
        <div className={`${styles}`}>
            <p className="font-semibold text-base text-[#0D0C22] truncate mb-2">{label}</p>
            
            {/* Botón de NimbusMath */}
            {showMathButton && !disabled && (
                <div className="flex items-center gap-2 mb-2">
                    <button
                        type="button"
                        onClick={() => openMathPanel()}
                        title="Insertar fórmula matemática (Nimbus Math)"
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-[#1a3d82] bg-white border border-[#c4c4c4] rounded hover:bg-gray-50 hover:border-[#999] transition-colors"
                    >
                        <span className="text-lg font-bold">√</span>
                        <span>Fórmula</span>
                    </button>
                </div>
            )}

            <div className={`editor-wrapper ${error === "" ? "" : "border-2 border-red-400 rounded-[4px]"}`}>
                <CKEditor
                    id={name}
                    editor={ClassicEditor}
                    data={processedValue}
                    disabled={disabled}
                    onReady={(editor) => {
                        editorRef.current = editor;
                    }}
                    config={{
                        placeholder: placeholder,
                        image: editorImageConfig,
                        fontSize: {
                            options: [9, 11, 13, "default", 17, 19, 25, 30, 35, 40],
                        },
                        extraPlugins: [MyCustomUploadAdapterPlugin, DataLatexPlugin, NimbusPasteStyleCleanupPlugin],
                        removePlugins: ["Title"],
                        mediaEmbed: { previewsInData: true },
                        link: editorLinkConfig,
                        toolbar: toolbarWithoutMathType,
                        list: listProperties,
                        htmlSupport: htmlSupportConfig,
                        heading: {
                            options: [
                                { model: "heading1", view: "h1", title: "Heading 1", class: "ck-heading_heading1" },
                                { model: "heading2", view: "h2", title: "Heading 2", class: "ck-heading_heading2" },
                                { model: "paragraph", title: "Paragraph", class: "ck-heading_paragraph" },
                            ],
                        },
                    }}
                    onChange={(event, editor) => {
                        const data = editor.getData();
                        // Mantener las imágenes con data-latex/data-mathml durante la edición
                        // La conversión a MathML puro se hace al guardar en la base de datos
                        let eventRichContentInput = {
                            target: {
                                name: name,
                                value: data,
                                type: "text",
                            },
                            persist: () => {},
                        };
                        onChange(eventRichContentInput);
                    }}
                />
            </div>
            <p className="font-thin text-xs text-red-400">{error}</p>
        </div>
    );
};

export default RichContentInputWithMath;
